/* ============================================================================
   OPALLAC | CART CAMPAIGN BANNER | assets/cart-campaign.js
   Version 2, single JSON configuration
   ----------------------------------------------------------------------------
   All behaviour AND all presentation for the cart drawer campaign banner.

   Everything is driven by one JSON block pasted into
   Theme settings -> Cart Campaign Banner -> Campaign configuration.
   Colours, fonts, copy, tiers, gift and Klaviyo all come from there. The
   Liquid snippet is an empty skeleton and contains no campaign values at all.

   Loaded ONCE from theme.liquid, so it is never destroyed when the cart drawer
   rebuilds itself.

   CUSTOM LAYOUTS
   The built in layout is optional. Paste your own markup into
   Theme settings -> Cart Campaign Banner -> Custom layout HTML and this engine
   will drive it instead. Every render function null checks its element, so a
   layout may include as much or as little as it likes. Available hooks:

     data-cc-name            campaign name
     data-cc-end             end date label
     data-cc-tagline         tagline
     data-cc-message         dynamic tier message
     data-cc-gift            gift panel wrapper
     data-cc-gift-flag       gift chip text
     data-cc-gift-title      gift title, may contain the value claim
     data-cc-gift-sub        gift subline, states the conditions
     data-cc-signup          signup wrapper
     data-cc-step="email"    email step wrapper
     data-cc-step="sms"      sms step wrapper
     data-cc-email           email input
     data-cc-phone           phone input
     data-cc-disclosure      sms consent wording
     data-cc-submit          submit button
     data-cc-skip            skip button
     data-cc-note            inline validation and status
     data-cc-tiers           ladder wrapper
     data-cc-tier-labels     container, JS builds the labels into it
     data-cc-tier-dots       container, JS builds the dots into it
     data-cc-tier-track      the rail track, JS sets its width
     data-cc-fill            the progress fill
     data-cc-label="5"       a specific tier label, if building them by hand
     data-cc-dot="5"         a specific tier dot, if building them by hand
     data-cc-text="a.b.c"    writes any config value into the element

   The banner root also carries state classes for CSS only styling:
     cc--locked / cc--unlocked / cc--gift-added / cc--has-pack
     cc--pack-0 / cc--pack-5 / cc--pack-10 ...
     cc--step-email / cc--step-sms / cc--step-done
   And every colour is a CSS custom property: --cc-accent, --cc-success,
   --cc-text, --cc-bg-dark, --cc-bg-mid, --cc-accent-lift.

   HARD-WON RULES, DO NOT UNDO:
   - /cart.js is read as TEXT then JSON.parse'd. Shopify serves it as
     text/javascript in some contexts; gating on content-type rejects a valid cart.
   - Pack size is read from the VARIANT OPTION first. The product handle is
     unreliable: every pack size shares one handle, which once reported every
     cart as a 15 pack.
   - Pack sizes are NEVER summed. Two 5 packs are not a 10 pack.
   - The discount code is applied BEFORE the gift is added, or the customer can
     be charged for it.
   - Klaviyo 'submit' only, never 'stepSubmit'.
   ========================================================================== */

(function () {
  'use strict';

  var CFG = null;
  var cachedCart = null;
  var debounceTimer = null;
  var mutating = false;
  var wired = false;
  var themedFor = null;   // JSON string of the theme block last applied

  var signup = { step: 'email', email: '', busy: false, note: '', noteOk: false };

  /* ==========================================================================
     CONFIG
     ========================================================================== */

  /* Decodes HTML entities. Shopify escapes textarea settings on output, so the
     configuration can arrive with &quot; instead of " and &amp; instead of &.
     Script tag content is never entity decoded by the browser, so we do it. */
  function decodeEntities(str) {
    if (str.indexOf('&') === -1) return str;
    var t = document.createElement('textarea');
    t.innerHTML = str;
    return t.value;
  }

  function readConfig() {
    var el = document.querySelector('[data-cc-config]');
    if (!el) {
      /* Expected on any page before the cart drawer has rendered, and whenever
         the campaign is switched off. Not a problem, so stay quiet. */
      return null;
    }

    var raw = (el.textContent || '');

    /* Shopify wraps every setting output in HTML comments while the theme
       editor is open:
         <!-- BEGIN setting: cart_campaign_config -->{...}<!-- END setting -->
       Those land inside our script tag and break JSON.parse on the first
       character. They are absent on the live storefront, which is why this
       only shows up in the customizer. Strip all comments, then take the
       outermost { ... } so any other stray wrapper is ignored too. */
    raw = raw.replace(/<!--[\s\S]*?-->/g, '').trim();
    var open = raw.indexOf('{');
    var close = raw.lastIndexOf('}');
    if (open !== -1 && close > open) raw = raw.slice(open, close + 1);

    if (!raw) {
      console.warn('[CartCampaign] Campaign configuration is empty. ' +
                   'Paste the JSON into Theme settings -> Cart Campaign Banner.');
      return null;
    }

    // Try as is, then entity decoded.
    try {
      return JSON.parse(raw);
    } catch (e1) {
      try {
        return JSON.parse(decodeEntities(raw));
      } catch (e2) {
        console.warn('[CartCampaign] Campaign configuration is not valid JSON. ' +
                     'Check Theme settings -> Cart Campaign Banner.\n' +
                     'Error: ' + e2.message + '\n' +
                     'Received (first 200 chars): ' + raw.slice(0, 200));
        return null;
      }
    }
  }

  function log() {
    if (!CFG || !CFG.debug) return;
    var a = Array.prototype.slice.call(arguments);
    a.unshift('[CartCampaign]');
    console.log.apply(console, a);
  }

  function banner() { return document.querySelector('[data-cc-banner]'); }

  function get(path, fallback) {
    var parts = path.split('.');
    var node = CFG;
    for (var i = 0; i < parts.length; i++) {
      if (node === null || typeof node !== 'object' || !(parts[i] in node)) return fallback;
      node = node[parts[i]];
    }
    return (node === undefined || node === null) ? fallback : node;
  }

  function money(cents) {
    return '$' + (Math.max(0, cents) / 100).toFixed(2);
  }

  // Replaces {min} and {short} placeholders in copy strings
  function fill(str, vals) {
    return String(str || '').replace(/\{(\w+)\}/g, function (m, k) {
      return (k in vals) ? vals[k] : m;
    });
  }

  /* ==========================================================================
     THEMING
     Colours, fonts and the theme mode come from config, so a rebrand is a
     config paste rather than a CSS edit.
     ========================================================================== */

  function loadFont(url) {
    if (!url) return;
    if (document.querySelector('link[data-cc-font]')) return;
    var l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = url;
    l.setAttribute('data-cc-font', '');
    document.head.appendChild(l);
  }

  function applyTheme(root) {
    var sig = JSON.stringify([get('theme'), get('colours'), get('fonts')]);
    if (sig === themedFor && root.getAttribute('data-cc-themed') === '1') return;
    themedFor = sig;

    var c = get('colours', {});
    var f = get('fonts', {});

    root.classList.remove('cc-banner--dark', 'cc-banner--light');
    root.classList.add('cc-banner--' + (get('theme', 'dark') === 'light' ? 'light' : 'dark'));

    var vars = {
      '--cc-bg-dark':     c.bgDark     || '#2E1A34',
      '--cc-bg-mid':      c.bgMid      || '#55305F',
      '--cc-text':        c.text       || '#ECE1F1',
      '--cc-accent':      c.accent     || '#C53C63',
      '--cc-accent-lift': c.accentLift || '#E8799A',
      '--cc-success':     c.success    || '#AFCB66',
      '--cc-font-head':   f.heading    || "'Poppins', Helvetica, Arial, sans-serif",
      '--cc-font-body':   f.body       || "'Fraunces', Georgia, serif"
    };
    Object.keys(vars).forEach(function (k) { root.style.setProperty(k, vars[k]); });

    // Accent at low alpha for the background wash, without needing another setting
    root.style.setProperty('--cc-accent-wash', (c.accent || '#C53C63') + '33');

    loadFont(f.googleUrl);
    root.setAttribute('data-cc-themed', '1');
  }

  /* ==========================================================================
     TIERS
     ========================================================================== */

  function tierItems() {
    var items = get('tiers.items', []);
    return Array.isArray(items) ? items.filter(function (t) {
      return t && typeof t.size === 'number';
    }) : [];
  }

  function tierSizes() {
    return tierItems().map(function (t) { return t.size; });
  }

  function validSize(n) {
    return tierSizes().indexOf(n) !== -1 ? n : 0;
  }

  function sizeFromPhrase(text) {
    var t = String(text || '').toLowerCase();
    var m = t.match(/(\d{1,2})\s*[-\s]?\s*pack/);
    if (m) return validSize(parseInt(m[1], 10));
    m = t.match(/pack\s*(?:of)?\s*[-\s]?\s*(\d{1,2})/);
    if (m) return validSize(parseInt(m[1], 10));
    return 0;
  }

  function variantStrings(item) {
    var out = [];
    if (item.variant_title) out.push(item.variant_title);
    if (Array.isArray(item.variant_options)) out = out.concat(item.variant_options);
    if (Array.isArray(item.options_with_values)) {
      item.options_with_values.forEach(function (o) { if (o && o.value) out.push(o.value); });
    }
    return out;
  }

  function detectSize(item) {
    var handles = get('tiers.packHandles', []);
    var map = get('tiers.variantMap', {});

    var handle = (item.handle || '').toLowerCase();
    var productTitle = (item.product_title || '').toLowerCase();
    var lineTitle = (item.title || '').toLowerCase();
    var nameText = handle + ' ' + productTitle + ' ' + lineTitle;

    var looksLikePack = handles.indexOf(handle) !== -1 || /pack|bundle/.test(nameText);

    // 1. Variant option, e.g. "Size: 5". Definitive when present.
    if (looksLikePack) {
      var bits = variantStrings(item);
      for (var i = 0; i < bits.length; i++) {
        var phrase = sizeFromPhrase(bits[i]);
        if (phrase) return phrase;
        var digits = String(bits[i]).match(/\d{1,2}/);
        if (digits) {
          var n = validSize(parseInt(digits[0], 10));
          if (n) return n;
        }
      }
    }

    // 2. Known variant ID. Fallback only.
    var vid = String(item.variant_id || '');
    if (vid && map[vid]) return validSize(map[vid]);

    // 3. Size in the TITLE. The handle is deliberately excluded.
    if (!looksLikePack) return 0;
    return sizeFromPhrase(productTitle + ' ' + lineTitle);
  }

  // Largest single pack sets the tier. Sizes are NOT summed.
  function largestPack(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce(function (best, item) {
      var size = detectSize(item);
      log('line', {
        variant_id: item.variant_id,
        handle: item.handle,
        variant_title: item.variant_title,
        size: size
      });
      return size > best ? size : best;
    }, 0);
  }

  function pctFor(size) {
    var found = tierItems().filter(function (t) { return t.size === size; })[0];
    return found ? (found.pct || '') : '';
  }

  function labelFor(size) {
    var found = tierItems().filter(function (t) { return t.size === size; })[0];
    return found ? (found.label || (size + ' Pack')) : '';
  }

  function nextSizeUp(size) {
    var sizes = tierSizes();
    var i = sizes.indexOf(size);
    return (i === -1 || i === sizes.length - 1) ? 0 : sizes[i + 1];
  }

  /* Dot centres for n tiers sit at (i + 0.5) / n. The track spans 0 to
     (n - 0.5) / n, so within the track the stop for tier i is
     (i + 0.5) / (n - 0.5). For four tiers that gives the familiar
     14.29 / 42.86 / 71.43 / 100. */
  function stopFor(size) {
    var sizes = tierSizes();
    var n = sizes.length;
    var i = sizes.indexOf(size);
    if (i === -1 || n < 2) return 0;
    return ((i + 0.5) / (n - 0.5)) * 100;
  }

  // Builds the ladder to match however many tiers the config defines
  function buildTiers(root) {
    var items = tierItems();
    var labels = root.querySelector('[data-cc-tier-labels]');
    var dots = root.querySelector('[data-cc-tier-dots]');
    var track = root.querySelector('[data-cc-tier-track]');
    if (!labels || !dots) return;

    var sig = JSON.stringify(items);
    if (labels.getAttribute('data-cc-sig') === sig) return;
    labels.setAttribute('data-cc-sig', sig);

    var cols = 'repeat(' + items.length + ', minmax(0, 1fr))';
    labels.style.gridTemplateColumns = cols;
    dots.style.gridTemplateColumns = cols;
    if (track) track.style.right = (100 / (2 * items.length)) + '%';

    labels.innerHTML = '';
    dots.innerHTML = '';

    items.forEach(function (t) {
      var l = document.createElement('div');
      l.className = 'cc-tiers__label';
      l.setAttribute('data-cc-label', String(t.size));
      l.innerHTML = '<span class="cc-tiers__qty"></span><span class="cc-tiers__pct"></span>';
      l.querySelector('.cc-tiers__qty').textContent = t.label || (t.size + ' Pack');
      l.querySelector('.cc-tiers__pct').textContent = (t.pct || '') + ' off';
      labels.appendChild(l);

      var d = document.createElement('div');
      d.className = 'cc-tiers__cell';
      d.setAttribute('data-cc-dot', String(t.size));
      d.innerHTML = '<span class="cc-tiers__dot"><span class="cc-tiers__check">&#10003;</span></span>';
      dots.appendChild(d);
    });
  }

  /* ==========================================================================
     GIFT
     ========================================================================== */

  function giftId() { return String(get('gift.variantId', '') || ''); }
  function giftOn() { return !!(get('gift.enabled', false) && giftId()); }
  function minSpend() { return get('gift.minSpend', 0) || 0; }
  function minLabel() { return get('gift.minLabel', ''); }

  function hasItems(cart) { return !!(cart && cart.items && cart.items.length); }

  function giftInCart(cart) {
    var id = giftId();
    if (!id || !cart || !cart.items) return false;
    return cart.items.some(function (i) { return String(i.variant_id) === id; });
  }

  function giftLineKey(cart) {
    var id = giftId();
    if (!id || !cart || !cart.items) return null;
    var f = cart.items.filter(function (i) { return String(i.variant_id) === id; })[0];
    return f ? f.key : null;
  }

  // Shopify has moved applied codes between fields across API versions
  function appliedCodes(cart) {
    var codes = [];
    if (!cart) return codes;
    if (Array.isArray(cart.discount_codes)) {
      cart.discount_codes.forEach(function (d) { if (d && d.code) codes.push(String(d.code)); });
    }
    if (Array.isArray(cart.cart_level_discount_applications)) {
      cart.cart_level_discount_applications.forEach(function (d) {
        if (d && d.title) codes.push(String(d.title));
        if (d && d.key) codes.push(String(d.key));
      });
    }
    (cart.items || []).forEach(function (i) {
      if (Array.isArray(i.discounts)) {
        i.discounts.forEach(function (d) { if (d && d.title) codes.push(String(d.title)); });
      }
      if (Array.isArray(i.line_level_discount_allocations)) {
        i.line_level_discount_allocations.forEach(function (a) {
          if (a && a.discount_application && a.discount_application.title) {
            codes.push(String(a.discount_application.title));
          }
        });
      }
    });
    return codes;
  }

  function isUnlocked(cart) {
    if (!giftOn()) return false;
    if (cart && cart.attributes && cart.attributes.cc_gift_unlocked) return true;
    var code = String(get('gift.code', '')).toLowerCase();
    if (!code) return false;
    return appliedCodes(cart).some(function (c) {
      return c.toLowerCase().indexOf(code) === 0;
    });
  }

  // Excludes the gift line so a zeroed gift cannot satisfy its own minimum
  function qualifyingTotal(cart) {
    var id = giftId();
    if (!cart || !cart.items) return 0;
    return cart.items.reduce(function (sum, i) {
      if (String(i.variant_id) === id) return sum;
      return sum + (i.final_line_price || i.line_price || 0);
    }, 0);
  }

  function meetsMinimum(cart) { return qualifyingTotal(cart) >= minSpend(); }

  function applyDiscount() {
    var code = get('gift.code', '');
    if (!code) return Promise.resolve();
    // Requesting the discount URL sets it on the session. Body is page HTML.
    return fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' })
      .catch(function (e) { console.warn('[CartCampaign] code not applied:', e.message); });
  }

  function addGiftLine() {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: giftId(), quantity: 1 })
    });
  }

  function removeLine(key) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
  }

  function afterCartWrite() {
    mutating = false;
    cachedCart = null;
    refresh();
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  // CODE FIRST, then the product.
  function grantGift() {
    if (mutating || !giftOn()) return;
    mutating = true;
    applyDiscount().then(addGiftLine).then(afterCartWrite)
      .catch(function (e) {
        mutating = false;
        console.warn('[CartCampaign] gift not granted:', e.message);
      });
  }

  function revokeGift(key) {
    if (mutating || !key) return;
    mutating = true;
    removeLine(key).then(afterCartWrite)
      .catch(function (e) {
        mutating = false;
        console.warn('[CartCampaign] gift not removed:', e.message);
      });
  }

  function enforceGift(cart) {
    if (!giftOn() || mutating) return false;
    var has = giftInCart(cart);
    var should = isUnlocked(cart) && hasItems(cart) &&
                 qualifyingTotal(cart) > 0 && meetsMinimum(cart);
    if (should && !has) { grantGift(); return true; }
    if (!should && has) { revokeGift(giftLineKey(cart)); return true; }
    return false;
  }

  /* ==========================================================================
     KLAVIYO
     ========================================================================== */

  function validEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || '').trim());
  }

  function normalisePhone(raw, cc) {
    var v = String(raw || '').replace(/[\s()\-.]/g, '');
    if (!v) return '';
    if (v.charAt(0) === '+') return v;
    if (v.charAt(0) === '0') return (cc || '+61') + v.slice(1);
    if (v.indexOf(String(cc || '+61').replace('+', '')) === 0) return '+' + v;
    return (cc || '+61') + v;
  }

  function validPhone(e164) { return /^\+[1-9]\d{7,14}$/.test(e164); }

  /* Klaviyo's client subscriptions endpoint takes ONE list per call, so
     subscribing to several means one request each.

     Config accepts either form:
       "listId":  "ShxSnk"                  one list
       "listIds": ["ShxSnk", "AbC123"]      several, first is the primary

     And optionally, lists joined ONLY when the SMS step completes:
       "smsListIds": ["XFNsUr"]
     Those never receive a profile without a phone number attached.

     The FIRST list must succeed or the signup is treated as failed and the
     customer sees an error. Any further lists are best effort: a failure there
     is logged but does not block the signup or the gift. That way a mistyped
     second list ID cannot stop people subscribing. */
  function asList(v) {
    if (Array.isArray(v)) return v.filter(function (id) { return !!id; });
    return v ? [String(v)] : [];
  }

  /* Lists everyone joins, at the email step. */
  function klaviyoLists() {
    var many = asList(get('klaviyo.listIds', null));
    if (many.length) return many;
    return asList(get('klaviyo.listId', ''));
  }

  /* Lists joined ONLY once the SMS step completes, so these never receive a
     profile without a phone number on it. */
  function klaviyoSmsLists() {
    var many = asList(get('klaviyo.smsListIds', null));
    if (many.length) return many;
    return asList(get('klaviyo.smsListId', ''));
  }

  function klaviyoPost(key, listId, attrs) {
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' +
                 encodeURIComponent(key), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: { profile: { data: { type: 'profile', attributes: attrs } } },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      })
    }).then(function (r) {
      // 202 Accepted with an empty body is success. Do not parse it.
      if (r.ok || r.status === 202) return true;
      // On failure Klaviyo returns a JSON body naming the problem. Surface it,
      // otherwise a bare status code tells you nothing useful.
      return r.text().then(function (body) {
        throw new Error('Klaviyo HTTP ' + r.status + ' ' + (body || '').slice(0, 300));
      });
    });
  }

  function klaviyo(attrs, lists) {
    var key = get('klaviyo.publicKey', '');
    lists = lists || klaviyoLists();
    if (!key || !lists.length) return Promise.reject(new Error('Klaviyo not configured'));

    // Secondary lists fire alongside the primary but never block it.
    lists.slice(1).forEach(function (id) {
      klaviyoPost(key, id, attrs).catch(function (e) {
        console.warn('[CartCampaign] secondary list ' + id + ' failed: ' + e.message);
      });
    });

    return klaviyoPost(key, lists[0], attrs);
  }

  /* Minimal payload, proven to return 202 against this account. Adding a
     subscriptions consent block here was rejected. The client subscriptions
     endpoint already implies email marketing consent by virtue of the call,
     so the block is unnecessary for the email step. */
  /* ===== Custom profile properties =====

     Anything under klaviyo.properties in the config is sent as a Klaviyo
     custom profile property. Klaviyo creates property names on first use, so
     nothing needs defining in advance.

       "klaviyo": {
         "captureUtm": true,
         "properties": {
           "utm_source": "cart_drawer",
           "utm_campaign": "spring_colour_sale"
         }
       }

     With captureUtm true, any real utm_* values the visitor arrived with are
     captured on first page load and override the static ones above. The static
     values then act as the fallback for direct and organic traffic.

     Note: reserved Klaviyo fields (first_name, last_name, organization, title,
     location) are TOP LEVEL attributes, not properties. Putting them here
     creates a duplicate custom field instead of filling the real one. */

  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  var UTM_STORE = 'cc_utm';

  /* sessionStorage throws in sandboxed iframes, which includes the Shopify
     theme editor preview. Never let that break a signup. */
  function safeSession(fn, fallback) {
    try { return fn(); } catch (e) { return fallback; }
  }

  /* Captured once per session on first load. By the time someone opens the
     cart drawer the URL has usually lost its UTM parameters. */
  function captureUtm() {
    if (!get('klaviyo.captureUtm', false)) return;
    var qs = window.location.search || '';
    if (!qs) return;

    var found = {};
    var any = false;
    UTM_KEYS.forEach(function (k) {
      var m = qs.match(new RegExp('[?&]' + k + '=([^&#]*)'));
      if (m && m[1]) {
        found[k] = decodeURIComponent(m[1].replace(/\+/g, ' '));
        any = true;
      }
    });
    if (!any) return;

    safeSession(function () {
      sessionStorage.setItem(UTM_STORE, JSON.stringify(found));
    });
  }

  function storedUtm() {
    return safeSession(function () {
      var raw = sessionStorage.getItem(UTM_STORE);
      return raw ? JSON.parse(raw) : {};
    }, {}) || {};
  }

  /* Static config properties first, real captured UTMs on top. */
  function profileProperties() {
    var out = {};
    var stat = get('klaviyo.properties', {});
    var k;
    for (k in stat) {
      if (Object.prototype.hasOwnProperty.call(stat, k)) out[k] = stat[k];
    }
    if (get('klaviyo.captureUtm', false)) {
      var live = storedUtm();
      for (k in live) {
        if (Object.prototype.hasOwnProperty.call(live, k)) out[k] = live[k];
      }
    }
    return out;
  }

  function withProperties(attrs) {
    var props = profileProperties();
    for (var k in props) {
      if (Object.prototype.hasOwnProperty.call(props, k)) {
        attrs.properties = attrs.properties || {};
        attrs.properties[k] = props[k];
      }
    }
    return attrs;
  }

  function subscribeEmail(email) {
    return klaviyo(withProperties({ email: email }));
  }

  /* Email and phone only. A 'subscriptions' consent block inside profile
     attributes is rejected by this API revision:
       "'subscriptions' is not a valid field for the resource 'profile'"
     Consent comes from the list subscription itself, so SMS consent must be
     enabled on the list in Klaviyo. Verify after the first real signup that the
     number lands as SUBSCRIBED rather than never consented. */
  /* Email lists again (so the profile is updated with the phone number) plus
     any SMS only lists. Duplicates are removed so a list named in both places
     is not posted to twice. */
  function subscribeSms(email, phone) {
    var lists = klaviyoLists().slice();
    klaviyoSmsLists().forEach(function (id) {
      if (lists.indexOf(id) === -1) lists.push(id);
    });
    return klaviyo(withProperties({ email: email, phone_number: phone }), lists);
  }

  function openPopup() {
    var id = get('klaviyo.formId', '');
    if (!id) return;
    if (window.klaviyo && typeof window.klaviyo.openForm === 'function') {
      window.klaviyo.openForm(id);
    } else if (window._klOnsite) {
      window._klOnsite.push(['openForm', id]);
    } else {
      console.warn('[CartCampaign] Klaviyo onsite script not loaded');
    }
  }

  function setNote(text, ok) { signup.note = text || ''; signup.noteOk = !!ok; }

  function finishSignup() {
    signup.step = 'done';
    signup.busy = false;
    setNote('', false);
    render();
    grantGift();
  }

  /* ==========================================================================
     RENDER
     ========================================================================== */

  function txt(el, v) { if (el) el.textContent = v; }
  function html(el, v) { if (el) el.innerHTML = v; }

  /* Generic binding for CUSTOM LAYOUTS.
     Any element can pull any value straight out of the config:
       <span data-cc-text="gift.valueLabel"></span>
       <span data-cc-text="tiers.basePct"></span>
     This is what lets a bespoke layout use config values without the JS
     needing to know anything about that layout's structure. */
  function bindConfigText(root) {
    var nodes = root.querySelectorAll('[data-cc-text]');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = get(nodes[i].getAttribute('data-cc-text'), '');
    }
  }

  /* State classes on the banner root, so a CUSTOM LAYOUT can respond in CSS
     alone without touching JS:
       .cc--locked / .cc--unlocked / .cc--gift-added
       .cc--pack-0 / .cc--pack-5 / .cc--pack-10 ...
       .cc--step-email / .cc--step-sms / .cc--step-done  */
  function applyStateClasses(root, size, cart) {
    var unlocked = isUnlocked(cart);
    var added = giftInCart(cart);

    root.classList.toggle('cc--locked', !unlocked);
    root.classList.toggle('cc--unlocked', unlocked);
    root.classList.toggle('cc--gift-added', added);
    root.classList.toggle('cc--has-pack', size > 0);

    var cls = root.className.split(/\s+/);
    for (var i = 0; i < cls.length; i++) {
      if (cls[i].indexOf('cc--pack-') === 0 || cls[i].indexOf('cc--step-') === 0) {
        root.classList.remove(cls[i]);
      }
    }
    root.classList.add('cc--pack-' + size);
    root.classList.add('cc--step-' + signup.step);
  }

  function renderStatic(root) {
    bindConfigText(root);
    txt(root.querySelector('[data-cc-name]'), get('name', ''));

    var endEl = root.querySelector('[data-cc-end]');
    var end = get('endLabel', '');
    if (endEl) {
      endEl.textContent = end;
      endEl.hidden = !end;
      var sep = root.querySelector('[data-cc-sep]');
      if (sep) sep.hidden = !end;
    }

    var tagEl = root.querySelector('[data-cc-tagline]');
    var tag = get('tagline', '');
    if (tagEl) { tagEl.textContent = tag; tagEl.hidden = !tag; }

    var emailIn = root.querySelector('[data-cc-email]');
    if (emailIn) emailIn.placeholder = get('copy.emailPlaceholder', 'name@email.com');

    var phoneIn = root.querySelector('[data-cc-phone]');
    if (phoneIn) phoneIn.placeholder = get('copy.phonePlaceholder', '04XX XXX XXX');

    var disc = root.querySelector('[data-cc-disclosure]');
    var d = get('klaviyo.smsDisclosure', '');
    if (disc) { disc.textContent = d; disc.hidden = !d; }

    txt(root.querySelector('[data-cc-skip]'), get('copy.skip', 'Skip'));
  }

  function renderTiers(root, size) {
    var wrap = root.querySelector('[data-cc-tiers]');
    if (!wrap) return;

    if (!get('tiers.enabled', false) || tierItems().length < 2) {
      wrap.hidden = true;
      return;
    }
    if (get('tiers.hideWhenEmpty', false) && !size) { wrap.hidden = true; return; }
    wrap.hidden = false;

    buildTiers(root);

    tierSizes().forEach(function (s) {
      var unlocked = size >= s;
      var l = root.querySelector('[data-cc-label="' + s + '"]');
      var d = root.querySelector('[data-cc-dot="' + s + '"]');
      if (l) l.classList.toggle('is-unlocked', unlocked);
      if (d) d.classList.toggle('is-unlocked', unlocked);
    });

    var f = root.querySelector('[data-cc-fill]');
    if (f) f.style.width = (size ? stopFor(size) : 0) + '%';
  }

  function renderMessage(root, size, cart) {
    var el = root.querySelector('[data-cc-message]');
    if (!el) return;

    if (!get('tiers.enabled', false) || tierItems().length < 2) { el.hidden = true; return; }
    el.hidden = false;

    var sizes = tierSizes();
    var top = sizes[sizes.length - 1];
    var first = sizes[0];
    var out;

    if (!size) {
      out = hasItems(cart)
        ? get('tiers.baseLabel', 'All products') + ' are <strong>' +
          get('tiers.basePct', '') + ' off</strong>. Add a ' + labelFor(first).toLowerCase() +
          ' to save <strong>' + pctFor(first) + '</strong>'
        : 'Add a pack to save up to <strong>' + pctFor(top) + '</strong>';
    } else if (size === top) {
      out = '<strong>Maximum ' + pctFor(top) + ' off unlocked!</strong>';
    } else {
      var up = nextSizeUp(size);
      out = pctFor(size) + ' off unlocked! Step up to a <strong>' +
            labelFor(up).toLowerCase() + '</strong> to save <strong>' + pctFor(up) + '</strong>';
    }
    html(el, out);
  }

  function giftTitle(lead) {
    var v = get('gift.valueLabel', '');
    return v ? (lead + ', <em>worth ' + v + '</em>') : lead;
  }

  function renderSignup(root, unlocked) {
    var wrap = root.querySelector('[data-cc-signup]');
    if (!wrap) return;

    var mode = get('klaviyo.mode', 'native');
    /* Use klaviyoLists(), not klaviyo.listId. The config may define either
       listId (single) or listIds (array), and checking only the old key hides
       the form whenever the array form is used. */
    var hide = unlocked || signup.step === 'done' || mode === 'off' ||
               (mode === 'native' && (!get('klaviyo.publicKey', '') || !klaviyoLists().length)) ||
               (mode === 'popup' && !get('klaviyo.formId', ''));

    wrap.hidden = hide;
    if (hide) return;

    var emailStep = root.querySelector('[data-cc-step="email"]');
    var smsStep = root.querySelector('[data-cc-step="sms"]');
    var submits = root.querySelectorAll('[data-cc-submit]');
    var skip = root.querySelector('[data-cc-skip]');
    var note = root.querySelector('[data-cc-note]');

    if (mode === 'popup') {
      if (emailStep) emailStep.hidden = true;
      if (smsStep) smsStep.hidden = true;
      for (var p = 0; p < submits.length; p++) {
        submits[p].hidden = false;
        submits[p].disabled = false;
        submits[p].textContent = get('copy.btnPopup', 'Subscribe to unlock');
      }
      if (skip) skip.hidden = true;
    } else {
      var onSms = signup.step === 'sms';
      if (emailStep) emailStep.hidden = onSms;
      if (smsStep) smsStep.hidden = !onSms;
      var label = signup.busy
        ? get('copy.btnBusy', 'Working')
        : (onSms ? get('copy.btnSms', 'Finish') : get('copy.btnEmail', 'Unlock'));
      for (var q = 0; q < submits.length; q++) {
        submits[q].hidden = false;
        submits[q].disabled = signup.busy;
        submits[q].textContent = label;
      }
      if (skip) skip.hidden = !(onSms && get('klaviyo.smsSkippable', false));

      // Restore the email if the drawer re-rendered mid flow
      var ei = root.querySelector('[data-cc-email]');
      if (ei && signup.email && !ei.value) ei.value = signup.email;
    }

    if (note) {
      note.textContent = signup.note;
      note.hidden = !signup.note;
      note.classList.toggle('is-ok', signup.noteOk);
    }
  }

  function renderGift(root, cart) {
    var panel = root.querySelector('[data-cc-gift]');
    if (!panel) return;

    if (!giftOn()) { panel.hidden = true; return; }
    panel.hidden = false;

    var added = giftInCart(cart);
    var unlocked = isUnlocked(cart);
    var name = get('gift.title', 'Free gift');
    var owned = 'Your ' + name.replace(/^free\s+/i, '');

    panel.classList.toggle('is-added', added);
    panel.classList.toggle('is-locked', !unlocked);

    var flag = root.querySelector('[data-cc-gift-flag]');
    var title = root.querySelector('[data-cc-gift-title]');
    var sub = root.querySelector('[data-cc-gift-sub]');
    var vals = { min: minLabel(), short: money(minSpend() - qualifyingTotal(cart)) };

    if (added) {
      txt(flag, get('gift.flagAdded', 'Added free'));
      html(title, giftTitle(owned));
      txt(sub, fill(get('copy.giftAdded', ''), vals));
    } else if (!unlocked) {
      txt(flag, get('gift.flagLocked', 'Free gift'));
      html(title, giftTitle(name));
      // State the spend condition up front, never after the email is given
      txt(sub, fill(get('copy.giftLocked', ''), vals));
    } else {
      txt(flag, get('gift.flagUnlocked', 'Unlocked'));
      html(title, giftTitle(owned));
      if (!hasItems(cart) || qualifyingTotal(cart) === 0) {
        txt(sub, fill(get('copy.giftNoCart', ''), vals));
      } else if (!meetsMinimum(cart)) {
        txt(sub, fill(get('copy.giftShort', ''), vals));
      } else {
        txt(sub, fill(get('copy.giftPending', ''), vals));
      }
    }

    renderSignup(root, unlocked);
  }

  function nudgeReflow(root) {
    if (!root) return;
    void root.offsetHeight;
    try { window.dispatchEvent(new Event('resize')); }
    catch (e) {
      var ev = document.createEvent('Event');
      ev.initEvent('resize', true, true);
      window.dispatchEvent(ev);
    }
  }

  function render() {
    var root = banner();
    if (!root || !CFG) return;

    applyTheme(root);
    renderStatic(root);

    if (!cachedCart) return;

    // A cart write is in flight. Skip this pass; the refresh that follows draws
    // the final state.
    if (enforceGift(cachedCart)) return;

    var size = get('tiers.enabled', false) ? largestPack(cachedCart) : 0;
    log('largest pack', size);

    applyStateClasses(root, size, cachedCart);
    renderTiers(root, size);
    renderMessage(root, size, cachedCart);
    renderGift(root, cachedCart);
    nudgeReflow(root);
  }

  /* ==========================================================================
     CART
     ========================================================================== */

  function refresh() {
    if (!banner()) return;
    fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart.js HTTP ' + r.status);
        // NOT r.json(), and no content-type check. Shopify serves this as
        // text/javascript in some contexts, which is still valid JSON.
        return r.text();
      })
      .then(function (t) {
        var cart;
        try { cart = JSON.parse(t); }
        catch (e) { throw new Error('cart.js body was not parseable JSON'); }
        cachedCart = cart;
        log('cart loaded, items:', (cart.items || []).length);
        render();
      })
      .catch(function (e) {
        console.warn('[CartCampaign] cart unavailable:', e.message);
      });
  }

  function debouncedRefresh() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      CFG = readConfig() || CFG;
      refresh();
    }, 200);
  }

  /* ==========================================================================
     EVENTS, all delegated so they survive the drawer rebuilding itself
     ========================================================================== */

  function handleSubmit() {
    var root = banner();
    if (!root || signup.busy || !CFG) return;

    if (get('klaviyo.mode', 'native') === 'popup') { openPopup(); return; }

    if (signup.step === 'email') {
      var ei = root.querySelector('[data-cc-email]');
      var email = ei ? ei.value.trim() : '';
      if (!validEmail(email)) {
        if (ei) ei.classList.add('has-error');
        setNote(get('copy.errEmail', 'Enter a valid email address'), false);
        render();
        if (ei) ei.focus();
        return;
      }
      if (ei) ei.classList.remove('has-error');

      signup.email = email;
      signup.busy = true;
      setNote('', false);
      render();

      subscribeEmail(email).then(function () {
        signup.busy = false;
        if (get('klaviyo.smsEnabled', false)) {
          signup.step = 'sms';
          setNote('', false);
          render();
        } else {
          finishSignup();
        }
      }).catch(function (e) {
        signup.busy = false;
        setNote(get('copy.errSubscribe', 'Could not subscribe just now.'), false);
        render();
        console.warn('[CartCampaign] subscribe failed:', e.message);
      });
      return;
    }

    if (signup.step === 'sms') {
      var pi = root.querySelector('[data-cc-phone]');
      var phone = normalisePhone(pi ? pi.value : '', get('klaviyo.countryCode', '+61'));
      if (!validPhone(phone)) {
        if (pi) pi.classList.add('has-error');
        setNote(get('copy.errPhone', 'Enter a valid mobile number'), false);
        render();
        if (pi) pi.focus();
        return;
      }
      if (pi) pi.classList.remove('has-error');

      signup.busy = true;
      setNote('', false);
      render();

      subscribeSms(signup.email, phone).then(finishSignup).catch(function (e) {
        signup.busy = false;
        setNote(get('copy.errSubscribe', 'Could not subscribe just now.'), false);
        render();
        console.warn('[CartCampaign] sms subscribe failed:', e.message);
      });
    }
  }

  function wire() {
    if (wired) return;
    wired = true;

    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      if (e.target.closest('[data-cc-submit]')) { e.preventDefault(); handleSubmit(); return; }
      if (e.target.closest('[data-cc-skip]')) { e.preventDefault(); finishSignup(); }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      if (!e.target || !e.target.matches) return;
      if (!e.target.matches('[data-cc-email], [data-cc-phone]')) return;
      e.preventDefault();
      handleSubmit();
    });

    document.addEventListener('input', function (e) {
      if (!e.target || !e.target.matches) return;
      if (!e.target.matches('[data-cc-email], [data-cc-phone]')) return;
      e.target.classList.remove('has-error');
      if (signup.note && !signup.noteOk) {
        setNote('', false);
        var root = banner();
        var n = root && root.querySelector('[data-cc-note]');
        if (n) { n.textContent = ''; n.hidden = true; }
      }
    });

    // 'submit' fires only on the FINAL step. 'stepSubmit' fires after every
    // step and must not be used, or the gift is granted after the email.
    window.addEventListener('klaviyoForms', function (e) {
      if (!e || !e.detail || !CFG) return;
      var id = get('klaviyo.formId', '');
      if (id && String(e.detail.formId) !== String(id)) return;
      if (e.detail.type === 'submit') finishSignup();
    });

    ['cart:refresh', 'cart:updated', 'ajaxCart:updated',
     'theme:cart:updated', 'cart:finished'].forEach(function (evt) {
      document.addEventListener(evt, debouncedRefresh);
    });

    // The drawer rebuilds on every cart change. Re-read config and re-render
    // when our banner reappears.
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if ((n.matches && n.matches('[data-cc-banner]')) ||
              (n.querySelector && n.querySelector('[data-cc-banner]'))) {
            CFG = readConfig() || CFG;
            themedFor = null;
            if (cachedCart) render(); else refresh();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    wire();
    CFG = readConfig();
    if (!CFG) return;
    captureUtm();
    log('init', CFG);
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
